/**
 * Enhanced Performance Monitoring Utility
 * 
 * This utility provides comprehensive functions to measure and track performance metrics
 * for both frontend operations and API calls with detailed tabular reporting.
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.marks = new Map();
    this.measures = new Map();
    this.apiMetrics = new Map();
    this.frontendMetrics = new Map();
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
  }

  /**
   * Generate unique session ID for tracking
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Start timing a performance metric
   * @param {string} label - The label for the metric
   * @param {Object} metadata - Additional metadata for the metric
   */
  start(label, metadata = {}) {
    const startTime = performance.now();
    const timestamp = new Date().toISOString();
    
    this.marks.set(`${label}-start`, startTime);
    
    // Store detailed metadata
    const metricData = {
      label,
      startTime,
      timestamp,
      sessionId: this.sessionId,
      metadata: {
        ...metadata,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp
      }
    };
    
    this.metrics.set(label, metricData);
    
    // Also use performance.mark for browser dev tools
    if (performance.mark) {
      performance.mark(`${label}-start`);
    }
    
    console.log(`🚀 Performance: Started measuring ${label}`, metadata);
  }

  /**
   * End timing a performance metric and log the result
   * @param {string} label - The label for the metric
   * @param {Object} additionalData - Additional data to store
   */
  end(label, additionalData = {}) {
    const endTime = performance.now();
    const startTime = this.marks.get(`${label}-start`);
    const metricData = this.metrics.get(label);
    
    if (startTime && metricData) {
      const duration = endTime - startTime;
      const endTimestamp = new Date().toISOString();
      
      // Update metric data with end information
      const completeMetricData = {
        ...metricData,
        endTime,
        duration,
        endTimestamp,
        additionalData,
        status: 'completed'
      };
      
      this.metrics.set(label, completeMetricData);
      
      // Categorize metric
      if (label.includes('api') || label.includes('fetch') || label.includes('update') || label.includes('save')) {
        this.apiMetrics.set(label, completeMetricData);
      } else {
        this.frontendMetrics.set(label, completeMetricData);
      }
      
      // Use performance.measure for browser dev tools
      if (performance.measure) {
        try {
          performance.mark(`${label}-end`);
          performance.measure(label, `${label}-start`, `${label}-end`);
        } catch (error) {
          console.warn('Performance measure failed:', error);
        }
      }
      
      // Log with color coding based on duration
      let logColor = '🟢'; // Green for good performance
      let performanceLevel = 'Good';
      if (duration > 1000) {
        logColor = '🔴'; // Red for slow performance
        performanceLevel = 'Poor';
      } else if (duration > 500) {
        logColor = '🟡'; // Yellow for moderate performance
        performanceLevel = 'Moderate';
      }
      
      console.log(`${logColor} Performance: ${label} took ${duration.toFixed(2)}ms (${performanceLevel})`);
      
      // Store in localStorage for persistence across sessions
      this.persistMetric(label, completeMetricData);
    } else {
      console.warn(`⚠️ Performance: No start time found for ${label}`);
    }
  }

  /**
   * Measure a function execution time with detailed tracking
   * @param {string} label - The label for the metric
   * @param {Function} fn - The function to measure
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<any>} The result of the function
   */
  async measure(label, fn, metadata = {}) {
    this.start(label, metadata);
    try {
      const result = await fn();
      this.end(label, { success: true, resultType: typeof result });
      return result;
    } catch (error) {
      this.end(label, { success: false, error: error.message, errorType: error.constructor.name });
      throw error;
    }
  }

  /**
   * Measure a synchronous function execution time
   * @param {string} label - The label for the metric
   * @param {Function} fn - The function to measure
   * @param {Object} metadata - Additional metadata
   * @returns {any} The result of the function
   */
  measureSync(label, fn, metadata = {}) {
    this.start(label, metadata);
    try {
      const result = fn();
      this.end(label, { success: true, resultType: typeof result });
      return result;
    } catch (error) {
      this.end(label, { success: false, error: error.message, errorType: error.constructor.name });
      throw error;
    }
  }

  /**
   * Get performance metrics for a specific label
   * @param {string} label - The label to get metrics for
   * @returns {Array} Array of recorded metric data
   */
  getMetrics(label) {
    const key = `perf_${label}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Get all performance metrics
   * @returns {Object} Object containing all metrics
   */
  getAllMetrics() {
    const allMetrics = {};
    for (const [label] of this.metrics) {
      allMetrics[label] = this.getMetrics(label);
    }
    return allMetrics;
  }

  /**
   * Get API metrics specifically
   * @returns {Object} Object containing API metrics
   */
  getApiMetrics() {
    const apiMetrics = {};
    for (const [label, data] of this.apiMetrics) {
      apiMetrics[label] = this.getMetrics(label);
    }
    return apiMetrics;
  }

  /**
   * Get frontend metrics specifically
   * @returns {Object} Object containing frontend metrics
   */
  getFrontendMetrics() {
    const frontendMetrics = {};
    for (const [label, data] of this.frontendMetrics) {
      frontendMetrics[label] = this.getMetrics(label);
    }
    return frontendMetrics;
  }

  /**
   * Clear all performance metrics
   */
  clearMetrics() {
    this.metrics.clear();
    this.marks.clear();
    this.apiMetrics.clear();
    this.frontendMetrics.clear();
    
    // Clear from localStorage
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('perf_')) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Generate a comprehensive performance report
   * @returns {Object} Detailed performance report with statistics
   */
  generateReport() {
    const allMetrics = this.getAllMetrics();
    const apiMetrics = this.getApiMetrics();
    const frontendMetrics = this.getFrontendMetrics();
    const report = {
      session: {
        sessionId: this.sessionId,
        startTime: new Date(this.startTime).toISOString(),
        duration: Date.now() - this.startTime,
        totalMetrics: Object.keys(allMetrics).length,
        apiMetrics: Object.keys(apiMetrics).length,
        frontendMetrics: Object.keys(frontendMetrics).length
      },
      summary: {},
      apiMetrics: {},
      frontendMetrics: {},
      detailedMetrics: {}
    };

    // Process all metrics
    for (const [label, metrics] of Object.entries(allMetrics)) {
      if (metrics.length > 0) {
        const sorted = metrics.sort((a, b) => a.duration - b.duration);
        const min = sorted[0].duration;
        const max = sorted[sorted.length - 1].duration;
        const avg = sorted.reduce((sum, val) => sum + val.duration, 0) / sorted.length;
        const median = sorted[Math.floor(sorted.length / 2)].duration;
        const successCount = sorted.filter(m => m.additionalData?.success !== false).length;
        const errorCount = sorted.length - successCount;

        const metricReport = {
          count: metrics.length,
          min: min.toFixed(2),
          max: max.toFixed(2),
          average: avg.toFixed(2),
          median: median.toFixed(2),
          latest: sorted[sorted.length - 1].duration.toFixed(2),
          successRate: ((successCount / metrics.length) * 100).toFixed(2),
          errorCount,
          successCount,
          performance: this.getPerformanceLevel(avg),
          lastExecuted: sorted[sorted.length - 1].endTimestamp,
          firstExecuted: sorted[0].timestamp
        };

        report.detailedMetrics[label] = metricReport;

        // Categorize
        if (label.includes('api') || label.includes('fetch') || label.includes('update') || label.includes('save')) {
          report.apiMetrics[label] = metricReport;
        } else {
          report.frontendMetrics[label] = metricReport;
        }
      }
    }

    // Generate summary statistics
    const allDurations = Object.values(allMetrics).flat().map(m => m.duration);
    if (allDurations.length > 0) {
      report.summary = {
        totalOperations: allDurations.length,
        averageResponseTime: (allDurations.reduce((a, b) => a + b, 0) / allDurations.length).toFixed(2),
        fastestOperation: Math.min(...allDurations).toFixed(2),
        slowestOperation: Math.max(...allDurations).toFixed(2),
        totalApiCalls: Object.keys(apiMetrics).length,
        totalFrontendOperations: Object.keys(frontendMetrics).length
      };
    }

    return report;
  }

  /**
   * Get performance level based on duration
   * @param {number} duration - Duration in milliseconds
   * @returns {string} Performance level
   */
  getPerformanceLevel(duration) {
    if (duration <= 200) return 'Excellent';
    if (duration <= 500) return 'Good';
    if (duration <= 1000) return 'Moderate';
    return 'Poor';
  }

  /**
   * Log a comprehensive performance report to console in tabular format
   */
  logReport() {
    const report = this.generateReport();
    
    console.group('📊 COMPREHENSIVE PERFORMANCE REPORT');
    
    // Session Info
    console.group('🔍 Session Information');
    console.table({
      'Session ID': report.session.sessionId,
      'Start Time': report.session.startTime,
      'Duration': `${report.session.duration}ms`,
      'Total Metrics': report.session.totalMetrics,
      'API Metrics': report.session.apiMetrics,
      'Frontend Metrics': report.session.frontendMetrics
    });
    console.groupEnd();

    // Summary
    if (report.summary.totalOperations) {
      console.group('📈 Summary Statistics');
      console.table({
        'Total Operations': report.summary.totalOperations,
        'Average Response Time': `${report.summary.averageResponseTime}ms`,
        'Fastest Operation': `${report.summary.fastestOperation}ms`,
        'Slowest Operation': `${report.summary.slowestOperation}ms`,
        'Total API Calls': report.summary.totalApiCalls,
        'Total Frontend Operations': report.summary.totalFrontendOperations
      });
      console.groupEnd();
    }

    // API Metrics Table
    if (Object.keys(report.apiMetrics).length > 0) {
      console.group('🌐 API Performance Metrics');
      const apiTable = Object.entries(report.apiMetrics).map(([label, data]) => ({
        'API Operation': label,
        'Count': data.count,
        'Average (ms)': data.average,
        'Min (ms)': data.min,
        'Max (ms)': data.max,
        'Success Rate (%)': data.successRate,
        'Performance': data.performance,
        'Last Executed': data.lastExecuted
      }));
      console.table(apiTable);
      console.groupEnd();
    }

    // Frontend Metrics Table
    if (Object.keys(report.frontendMetrics).length > 0) {
      console.group('⚡ Frontend Performance Metrics');
      const frontendTable = Object.entries(report.frontendMetrics).map(([label, data]) => ({
        'Operation': label,
        'Count': data.count,
        'Average (ms)': data.average,
        'Min (ms)': data.min,
        'Max (ms)': data.max,
        'Success Rate (%)': data.successRate,
        'Performance': data.performance,
        'Last Executed': data.lastExecuted
      }));
      console.table(frontendTable);
      console.groupEnd();
    }

    // Detailed Metrics
    console.group('📋 Detailed Metrics (All Operations)');
    const detailedTable = Object.entries(report.detailedMetrics).map(([label, data]) => ({
      'Operation': label,
      'Type': label.includes('api') || label.includes('fetch') || label.includes('update') || label.includes('save') ? 'API' : 'Frontend',
      'Count': data.count,
      'Average (ms)': data.average,
      'Min (ms)': data.min,
      'Max (ms)': data.max,
      'Median (ms)': data.median,
      'Success Rate (%)': data.successRate,
      'Performance': data.performance,
      'Last Executed': data.lastExecuted
    }));
    console.table(detailedTable);
    console.groupEnd();

    console.groupEnd();
  }

  /**
   * Persist a metric to localStorage with detailed data
   * @param {string} label - The metric label
   * @param {Object} metricData - The complete metric data
   */
  persistMetric(label, metricData) {
    const key = `perf_${label}`;
    const existing = localStorage.getItem(key);
    const metrics = existing ? JSON.parse(existing) : [];
    
    // Keep only the last 50 measurements to prevent localStorage bloat
    metrics.push(metricData);
    if (metrics.length > 50) {
      metrics.shift();
    }
    
    localStorage.setItem(key, JSON.stringify(metrics));
  }

  /**
   * Monitor React component render performance
   * @param {string} componentName - The name of the component
   * @param {Function} component - The component to monitor
   * @returns {Function} Wrapped component with performance monitoring
   */
  monitorComponent(componentName, component) {
    return (...props) => {
      const startTime = performance.now();
      const result = component(...props);
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const metricData = {
        label: `${componentName}-render`,
        startTime,
        endTime,
        duration,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        metadata: {
          componentName,
          props: Object.keys(props),
          userAgent: navigator.userAgent,
          url: window.location.href
        },
        additionalData: {
          success: true,
          resultType: typeof result
        },
        status: 'completed'
      };
      
      this.metrics.set(`${componentName}-render`, metricData);
      this.frontendMetrics.set(`${componentName}-render`, metricData);
      this.persistMetric(`${componentName}-render`, metricData);
      
      if (duration > 16) { // 60fps threshold
        console.warn(`⚠️ Slow render detected: ${componentName} took ${duration.toFixed(2)}ms`);
      }
      
      return result;
    };
  }

  /**
   * Monitor API call performance with detailed tracking
   * @param {string} apiName - The name of the API
   * @param {Function} apiCall - The API call function
   * @returns {Function} Wrapped API call with performance monitoring
   */
  monitorApiCall(apiName, apiCall) {
    return async (...args) => {
      return this.measure(`${apiName}-api`, () => apiCall(...args), {
        apiName,
        args: args.length,
        timestamp: new Date().toISOString()
      });
    };
  }

  /**
   * Export performance data as JSON
   * @returns {string} JSON string of all performance data
   */
  exportData() {
    const report = this.generateReport();
    return JSON.stringify(report, null, 2);
  }

  /**
   * Get real-time metrics for dashboard
   * @returns {Object} Current metrics for dashboard display
   */
  getDashboardMetrics() {
    const report = this.generateReport();
    return {
      session: report.session,
      summary: report.summary,
      apiMetrics: report.apiMetrics,
      frontendMetrics: report.frontendMetrics,
      detailedMetrics: report.detailedMetrics,
      timestamp: new Date().toISOString()
    };
  }
}

// Create a singleton instance
const performanceMonitor = new PerformanceMonitor();

// Export both the class and the singleton instance
export { PerformanceMonitor };
export default performanceMonitor;
